import { useCallback, useEffect, useMemo, useState } from "react";
import { getPhoneNumbersByClientId } from "../../../core/api/clientsService";
import { getPhoneNumberById } from "../../numbers/services/numbersService";

const useVoicemailNumbers = ({ users, selectedUser, clientId }) => {
  const [phoneNumbers, setPhoneNumbers] = useState([]);

  const loadPhoneNumbers = useCallback(async () => {
    if (!clientId) {
      setPhoneNumbers([]);
      return;
    }

    const response = await getPhoneNumbersByClientId(clientId, {
      limit: 100,
      offset: 0,
    });

    if (response?.error) {
      setPhoneNumbers([]);
      return;
    }

    setPhoneNumbers(Array.isArray(response?.phoneNumbers) ? response.phoneNumbers : []);
  }, [clientId]);

  useEffect(() => {
    loadPhoneNumbers();
  }, [loadPhoneNumbers]);

  const phoneNumberLookup = useMemo(() => {
    return new Map(phoneNumbers.map((record) => [Number(record.id), record]));
  }, [phoneNumbers]);

  const resolveVoicemailLabel = useCallback((record) => {
    if (!record) return "";
    const phoneValue = record.phone_number || record.phoneNumber || "";
    const friendlyName = record.friendly_name || record.friendlyName || "";
    if (friendlyName && friendlyName !== phoneValue) {
      return `${friendlyName} (${phoneValue})`;
    }
    return phoneValue;
  }, []);

  const assignedVoicemailIds = useMemo(() => {
    const ids = new Set();
    users.forEach((user) => {
      const rawId = Number(user.voicemailNumberId ?? user.voicemail_number_id);
      if (Number.isFinite(rawId) && rawId > 0) {
        ids.add(rawId);
      }
    });
    return ids;
  }, [users]);

  const usersWithVoicemailNumbers = useMemo(() => {
    return users.map((user) => {
      const rawId = Number(user.voicemailNumberId ?? user.voicemail_number_id);
      const voicemailNumberId =
        Number.isFinite(rawId) && rawId > 0 ? rawId : null;
      const record = voicemailNumberId
        ? phoneNumberLookup.get(Number(voicemailNumberId))
        : null;
      const voicemailNumber = record?.phone_number || record?.phoneNumber || "";

      return {
        ...user,
        voicemailNumberId,
        voicemailNumber,
      };
    });
  }, [users, phoneNumberLookup]);

  const availableVoicemailNumbers = useMemo(() => {
    return phoneNumbers.filter((record) => {
      const recordId = Number(record?.id);
      if (Number.isFinite(recordId) && assignedVoicemailIds.has(recordId)) {
        return false;
      }
      return !record?.is_associated;
    });
  }, [phoneNumbers, assignedVoicemailIds]);

  const hydrateMissingVoicemailNumbers = useCallback(async () => {
    const missingIds = Array.from(assignedVoicemailIds).filter(
      (id) => !phoneNumberLookup.has(id),
    );

    if (missingIds.length === 0) {
      return;
    }

    const responses = await Promise.all(
      missingIds.map((id) => getPhoneNumberById(id)),
    );

    const fetched = responses
      .filter((response) => response && !response.error)
      .map((response) => {
        if (Array.isArray(response?.phone_numbers)) {
          return response.phone_numbers[0];
        }
        return response?.data || response;
      })
      .filter((record) => record && record.id);

    if (fetched.length === 0) {
      return;
    }

    setPhoneNumbers((current) => {
      const existing = new Map(current.map((record) => [Number(record.id), record]));
      fetched.forEach((record) => {
        existing.set(Number(record.id), record);
      });
      return Array.from(existing.values());
    });
  }, [assignedVoicemailIds, phoneNumberLookup]);

  useEffect(() => {
    hydrateMissingVoicemailNumbers();
  }, [hydrateMissingVoicemailNumbers]);

  useEffect(() => {
    const voicemailId = Number(selectedUser?.voicemailNumberId);
    if (!Number.isFinite(voicemailId) || voicemailId <= 0) {
      return;
    }

    if (phoneNumberLookup.has(voicemailId)) {
      return;
    }

    getPhoneNumberById(voicemailId).then((response) => {
      if (!response || response.error) return;
      const record = response?.data || response;
      if (!record || !record.id) return;

      setPhoneNumbers((current) => {
        const existing = new Map(
          current.map((item) => [Number(item.id), item]),
        );
        existing.set(Number(record.id), record);
        return Array.from(existing.values());
      });
    });
  }, [selectedUser, phoneNumberLookup]);

  const editVoicemailNumbers = useMemo(() => {
    if (!selectedUser?.voicemailNumberId) {
      return availableVoicemailNumbers;
    }

    return phoneNumbers.filter((record) => {
      const recordId = Number(record?.id);
      if (Number(recordId) === Number(selectedUser.voicemailNumberId)) {
        return true;
      }

      if (Number.isFinite(recordId) && assignedVoicemailIds.has(recordId)) {
        return false;
      }

      return !record?.is_associated;
    });
  }, [availableVoicemailNumbers, phoneNumbers, selectedUser, assignedVoicemailIds]);

  const selectedVoicemailLabel = useMemo(() => {
    if (!selectedUser?.voicemailNumberId) return "";
    const record = phoneNumberLookup.get(Number(selectedUser.voicemailNumberId));
    return resolveVoicemailLabel(record) || selectedUser.voicemailNumber || "";
  }, [selectedUser, phoneNumberLookup, resolveVoicemailLabel]);

  return {
    loadPhoneNumbers,
    usersWithVoicemailNumbers,
    availableVoicemailNumbers,
    editVoicemailNumbers,
    selectedVoicemailLabel,
  };
};

export default useVoicemailNumbers;
